/* The sign-in screen — ported from the prototype's RegistrationGate card (code.txt
   1064-1091) with the real two-step flow: request a code by email, then enter it.
   Google sign-in is rendered only when the server says it's configured. */

import { api } from "../api.js";
import { state } from "../state.js";

const EMAIL_RE = /.+@.+\..+/;
let pendingEmail = null;

/* ---------- markup ---------- */

const logoMarkup = `
  <div style="display:flex;align-items:center;gap:10px;justify-content:center;margin-bottom:26px">
    <div style="position:relative;display:inline-flex;width:34px;height:30px;flex-shrink:0">
      <svg width="30" height="30" viewBox="0 0 100 100" fill="none" style="position:absolute;inset:0">
        <line x1="45" y1="46" x2="34" y2="26" stroke="#5509F8" stroke-width="7" stroke-linecap="round"/>
        <circle cx="31" cy="20" r="11" fill="#5509F8"/>
        <line x1="55" y1="46" x2="64" y2="30" stroke="#5509F8" stroke-width="7" stroke-linecap="round"/>
        <circle cx="67" cy="25" r="9" fill="#5509F8"/>
        <rect x="6" y="42" width="88" height="52" rx="26" fill="#5509F8"/>
        <path d="M26 72 L35 63 L44 72" stroke="#fff" stroke-width="7" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M56 72 L65 63 L74 72" stroke="#fff" stroke-width="7" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <svg width="15" height="15" viewBox="0 0 100 100" style="position:absolute;top:-5px;right:-2px">
        <path d="M50 0 C53.5 27.5 72.5 46.5 100 50 C72.5 53.5 53.5 72.5 50 100 C46.5 72.5 27.5 53.5 0 50 C27.5 46.5 46.5 27.5 50 0 Z" fill="#2E8FD8"/>
      </svg>
    </div>
    <div>
      <div style="font-size:14px;font-weight:800;letter-spacing:-0.3px"><span style="color:var(--violet)">AIO Gap</span> Finder</div>
      <div style="font-size:9.5px;color:var(--muted);font-weight:600;letter-spacing:0.5px;text-transform:uppercase">Free tool by Botpresso</div>
    </div>
  </div>`;

function shell(inner) {
  return `
    <div style="min-height:100vh;background:var(--bg);display:flex;align-items:center;justify-content:center;padding:24px;font-family:var(--font)">
      <div style="width:100%;max-width:420px">
        ${logoMarkup}
        <div style="background:var(--surface);border:1px solid var(--line);border-radius:16px;padding:28px">${inner}</div>
      </div>
    </div>`;
}

/* ---------- screen ---------- */

export function renderAuthScreen(container) {
  pendingEmail = null;
  container.innerHTML = shell(`
    <h1 style="font-size:20px;font-weight:800;letter-spacing:-0.4px;margin:0 0 6px;color:var(--ink)">Create your free account</h1>
    <p style="font-size:12.5px;color:var(--sub);margin:0 0 18px;line-height:1.55">
      Registration is required to use the tool. Your analyses and settings (like your DataForSEO key) are saved to your account.
    </p>
    <button id="btn-google" style="width:100%;display:flex;align-items:center;justify-content:center;gap:9px;padding:10px 14px;border-radius:11px;border:1px solid var(--line);background:var(--surface);font-family:var(--font);font-size:13px;font-weight:700;color:var(--ink);cursor:pointer;margin-bottom:12px">
      <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
      Continue with Google
    </button>
    <div style="display:flex;align-items:center;gap:10px;margin:2px 0 12px">
      <div style="flex:1;height:1px;background:var(--line-soft)"></div>
      <span style="font-size:10.5px;color:var(--muted);font-weight:600">or with email</span>
      <div style="flex:1;height:1px;background:var(--line-soft)"></div>
    </div>
    <div id="auth-email-step"></div>
    <div style="font-size:10.5px;color:var(--muted);margin-top:12px;text-align:center;line-height:1.5">
      Your account, uploads, and saved scans are stored securely on Botpresso servers.
    </div>
  `);
  wireGoogle(container);
  renderStep(container, "email");
  container.querySelector("#auth-email-step").querySelector("input")?.focus();
}

/* ---------- email two-step flow ---------- */

function renderStep(container, step) {
  const box = container.querySelector("#auth-email-step");
  const email = pendingEmail || "";
  const err = box.dataset.err || "";
  delete box.dataset.err;

  if (step === "email") {
    box.innerHTML = `
      <input id="auth-email" type="email" value="${email}" placeholder="you@company.com" autocomplete="email"
        style="width:100%;padding:10px 12px;border-radius:11px;border:1px solid ${err ? "var(--red)" : "var(--line)"};background:var(--bg);font-family:var(--font);font-size:13px;color:var(--ink);outline:none;margin-bottom:${err ? 4 : 12}px;box-sizing:border-box"/>
      ${err ? `<div style="font-size:11.5px;color:var(--red);margin-bottom:10px">${err}</div>` : ""}
      <button id="btn-email" style="width:100%;justify-content:center;background:var(--violet);color:#fff;border:none;border-radius:11px;font-family:var(--font);font-weight:700;padding:11px 20px;font-size:13.5px;cursor:pointer;display:flex;align-items:center;gap:7px">
        Request sign-in code
      </button>`;
  } else {
    const devHint = box.dataset.devCode
      ? `<div style="font-size:11px;color:var(--blue);background:var(--blue-soft);border-radius:9px;padding:8px 10px;margin-bottom:10px;font-weight:600">
           Development mode — your code is <span style="font-family:var(--mono)">${box.dataset.devCode}</span>
         </div>`
      : "";
    box.innerHTML = `
      <div style="font-size:12.5px;color:var(--sub);margin-bottom:10px;line-height:1.5">Enter the 6-digit code sent to <b>${email}</b> — it expires in 5 minutes.</div>
      ${devHint}
      <input id="auth-code" type="text" inputmode="numeric" maxlength="6" placeholder="000000" autocomplete="one-time-code"
        style="width:100%;padding:10px 12px;border-radius:11px;border:1px solid ${err ? "var(--red)" : "var(--line)"};background:var(--bg);font-family:var(--mono);font-size:15px;letter-spacing:3px;color:var(--ink);outline:none;margin-bottom:${err ? 4 : 12}px;box-sizing:border-box;text-align:center"/>
      ${err ? `<div style="font-size:11.5px;color:var(--red);margin-bottom:10px">${err}</div>` : ""}
      <button id="btn-email" style="width:100%;justify-content:center;background:var(--violet);color:#fff;border:none;border-radius:11px;font-family:var(--font);font-weight:700;padding:11px 20px;font-size:13.5px;cursor:pointer;display:flex;align-items:center;gap:7px">
        Sign in
      </button>
      <button id="btn-back" style="border:none;background:transparent;color:var(--muted);font-family:var(--font);font-size:12px;font-weight:600;cursor:pointer;padding:10px 0 0;width:100%">← Use a different email</button>`;
  }

  const btn = box.querySelector("#btn-email");
  btn.addEventListener("click", () => (step === "email" ? requestCode(container) : verify(container)));
  const input = box.querySelector(step === "email" ? "#auth-email" : "#auth-code");
  input.addEventListener("keydown", e => { if (e.key === "Enter") btn.click(); });
  if (step === "code") {
    input.addEventListener("input", () => {
      if (input.value.replace(/\D/g, "").length === 6) btn.click();
    });
    box.querySelector("#btn-back").addEventListener("click", () => { pendingEmail = null; renderStep(container, "email"); });
  }
  box.dataset.step = step;
}

function fail(container, msg) {
  const box = container.querySelector("#auth-email-step");
  box.dataset.err = msg;
  renderStep(container, box.dataset.step);
}

async function requestCode(container) {
  const email = container.querySelector("#auth-email").value.trim();
  if (!EMAIL_RE.test(email)) { fail(container, "Enter a valid email address to continue."); return; }
  try {
    const res = await api.emailStart(email);
    pendingEmail = email;
    const box = container.querySelector("#auth-email-step");
    box.dataset.devCode = res.devCode || "";
    renderStep(container, "code");
  } catch (err) {
    fail(container, err.message);
  }
}

async function verify(container) {
  const code = (container.querySelector("#auth-code").value || "").replace(/\D/g, "");
  if (code.length !== 6) { fail(container, "Enter the 6-digit code"); return; }
  try {
    const { user } = await api.emailVerify(pendingEmail, code);
    state.user = user;
    window.dispatchEvent(new CustomEvent("agf:signed-in", { detail: { user } }));
  } catch (err) {
    fail(container, err.message);
  }
}

/* ---------- Google ---------- */

function wireGoogle(container) {
  const btn = container.querySelector("#btn-google");
  if (!state.googleEnabled) {
    btn.disabled = true;
    btn.style.opacity = 0.55;
    btn.style.cursor = "not-allowed";
    btn.title = "Google sign-in isn't configured yet — continue with email";
    btn.addEventListener("click", () => fail(container, "Google sign-in isn't configured yet — continue with email"));
    return;
  }
  btn.addEventListener("click", async () => {
    try {
      await loadGoogleIdentity(container);
      window.google.accounts.id.prompt(notification => {
        const skipped = notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.();
        if (skipped) showGoogleButton(container);
      });
    } catch {
      fail(container, "Google sign-in could not start");
    }
  });
}

function showGoogleButton(container) {
  if (document.getElementById("gsi-button")) return;
  const ours = container.querySelector("#btn-google");
  const holder = document.createElement("div");
  holder.id = "gsi-button";
  holder.style.display = "flex";
  holder.style.justifyContent = "center";
  holder.style.padding = "6px 0 12px";
  ours.parentNode.insertBefore(holder, ours);
  ours.classList.add("hidden");
  ours.style.display = "none";
  window.google.accounts.id.renderButton(holder, {
    theme: "outline", size: "large", width: 340, text: "continue_with", shape: "pill",
  });
}

function loadGoogleIdentity(container) {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => {
      window.google.accounts.id.initialize({
        client_id: state.googleClientId,
        callback: async ({ credential }) => {
          try {
            const { user } = await api.google(credential);
            state.user = user;
            window.dispatchEvent(new CustomEvent("agf:signed-in", { detail: { user } }));
          } catch (err) {
            fail(container, err.message);
          }
        },
      });
      resolve();
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });
}